import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '@guryou/colors';
import { NewsLetterImage } from '@guryou/html-components';

const NewsLetters = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);

    // Simulate API call
    setTimeout(() => {
      setIsSubscribed(true);
      setIsLoading(false);
      setEmail('');
    }, 2000);
  };

  return (
    <section
      className="py-16 lg:py-24 relative overflow-hidden"
      style={{
        backgroundImage: `url(${NewsLetterImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0" style={{ backgroundColor: `${colors.mineShaft}90` }}></div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          {/* Content Container with Blur Background */}
          <div
            className="relative backdrop-blur-md rounded-3xl p-8 lg:p-12 border border-white/20 shadow-2xl"
            style={{ backgroundColor: 'color-mix(in srgb, var(--feel-primary) 20%, transparent)' }}
          >
            {/* Icon */}
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 backdrop-blur-sm"
              style={{ backgroundColor: `${colors.carouselPink}80` }}
            >
              <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 20 20" style={{ color: colors.white }}>
                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
              </svg>
            </div>

            {/* Title */}
            <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-white drop-shadow-lg">{t('NEWSLETTER.newsletterTitle')}</h2>

            {/* Description */}
            <p className="text-xl text-white max-w-3xl mx-auto leading-relaxed mb-8 drop-shadow-md">{t('NEWSLETTER.description')}</p>

            {/* Success Message */}
            {isSubscribed && (
              <div className="mb-8 p-4 rounded-lg backdrop-blur-sm" style={{ backgroundColor: `${colors.carouselPink}90` }}>
                <p className="text-white font-medium">{t('NEWSLETTER.successMessage')}</p>
              </div>
            )}

            {/* Newsletter Form */}
            <div className="max-w-md mx-auto">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="relative flex-1">
                    <label htmlFor="email" className="sr-only">
                      {t('NEWSLETTER.emailLabel')}
                    </label>
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" style={{ color: colors.mineShaft }}>
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"></path>
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"></path>
                      </svg>
                    </div>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      className={`block w-full pl-10 pr-3 py-3 border rounded-lg bg-white/95 backdrop-blur-sm text-gray-900 placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:border-transparent sm:rounded-r-none border-[${colors.mercury}] focus:ring-[var(--feel-primary)]`}
                      placeholder={t('NEWSLETTER.newsletterPlaceholder')}
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading || !email}
                    className={`px-6 py-3 text-white font-medium rounded-lg transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed sm:rounded-l-none backdrop-blur-sm bg-[var(--feel-primary)] hover:bg-[var(--feel-primary-dark)]`}
                  >
                    {isLoading ? (
                      <div className="flex items-center">
                        <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                        {t('NEWSLETTER.subscribing')}
                      </div>
                    ) : (
                      t('NEWSLETTER.subscribe')
                    )}
                  </button>
                </div>

                {/* Privacy Notice */}
                <div className="text-sm text-white text-center drop-shadow-sm">
                  {t('NEWSLETTER.privacyNotice')}{' '}
                  <a href="/privacy" className="font-medium hover:underline drop-shadow-sm" style={{ color: colors.carouselPink }}>
                    {t('NEWSLETTER.privacyPolicy')}
                  </a>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewsLetters;
